import React from "react";
import { LayoutRectangle, Modal, SafeAreaView, View } from "react-native";
import { ImageViewHeaderProps, ImageViewHeader } from "./ImageViewHeader";
import { Image } from "expo-image";
import { ReactNativeZoomableView } from "@openspacelabs/react-native-zoomable-view";
import { PagerView } from "./PagerView";
import { Icon, Text, useTheme } from "react-native-paper";
import { DocumentReference, DocumentSnapshot } from "@atgof-firebase/types";
import { useImage } from "../data/image";
import { PhraseFunction } from "@atgof-common/phrases";
import { LanguageContext } from "@atgof-common/language";
import ErrorBoundary from "./ErrorBoundary";
import { useSnapshot } from "../data/useSnapshot";
import { onUserName } from "@atgof-common/membership";
import { useProject } from "../data/useProject";
import _ from "lodash";
import { formatDate, formatTime, HOUR_MILLIS } from "@atgof-common/util";

function UploadStatus({
  uploadStartedMillis,
  uploadedBy,
  isStale,
}: {
  uploadStartedMillis: number | undefined;
  uploadedBy: DocumentReference | undefined;
  isStale: boolean;
}) {
  const { ph } = React.useContext(LanguageContext);
  const project = useProject();
  const user = useSnapshot<string | undefined>(
    _.curry(onUserName)(project, uploadedBy),
    [project, uploadedBy?.path],
  );
  if (!(uploadStartedMillis && user)) return null;
  const d = new Date(uploadStartedMillis);
  const datetime = formatDate(d) + " " + formatTime(d);
  return (
    <View style={{ maxWidth: 380, gap: 18 }}>
      <Text variant="bodyLarge">
        {
          (ph("upload-started-by") as PhraseFunction)({
            user,
            datetime,
          }) as string
        }
      </Text>
      {isStale ? (
        <Text variant="bodyLarge">
          {ph("check-with-them-retry-prompt") as string}
        </Text>
      ) : null}
    </View>
  );
}

function ZoomableImage({
  doc,
  referencePath,
  onLoad,
}: {
  doc: DocumentSnapshot;
  referencePath: string | undefined;
  onLoad: () => void;
}) {
  const { image, registerAsCached, registerFailure, registerCompletion } =
    useImage(doc, referencePath);
  const zoomable = React.useRef<ReactNativeZoomableView>(null);
  const [imageLayout, setImageLayout] = React.useState<LayoutRectangle>();
  if (!image) return null;
  const { source, blurhash, thumbnailSource } = image;
  if (typeof image.inProgress === "object") {
    const { uploadStartedMillis, uploadedBy } = image.inProgress;
    const isStale = !(
      new Date().getTime() - (image.inProgress.uploadStartedMillis ?? 0) <
      HOUR_MILLIS
    );
    return (
      <ErrorBoundary>
        <View
          style={{
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            gap: 18,
            margin: 12,
          }}
        >
          <Icon
            source={isStale ? "progress-alert" : "progress-upload"}
            size={64}
          />
          <UploadStatus
            uploadStartedMillis={uploadStartedMillis}
            uploadedBy={uploadedBy}
            isStale={isStale}
          />
        </View>
      </ErrorBoundary>
    );
  }
  return (
    <ReactNativeZoomableView
      ref={zoomable}
      style={{ width: "100%", height: "100%" }}
      initialZoom={1}
      bindToBorders={true}
      disablePanOnInitialZoom={true}
      minZoom={1}
      maxZoom={10}
      contentWidth={imageLayout?.width}
      contentHeight={imageLayout?.height}
    >
      <Image
        onLayout={(evt) => setImageLayout(evt.nativeEvent.layout)}
        placeholder={{
          blurhash,
          ...thumbnailSource,
          width: source.width,
          height: source.height,
        }}
        placeholderContentFit="contain"
        source={source}
        cachePolicy="disk"
        priority="high"
        contentFit="contain"
        style={{
          width: "100%",
          height: "100%",
        }}
        onLoad={(evt) => {
          registerAsCached(evt.cacheType === "disk");
          registerCompletion();
          onLoad();
        }}
        onError={(evt) => {
          registerFailure(evt.error);
        }}
      />
    </ReactNativeZoomableView>
  );
}

export default function ImageView({
  images,
  imageIndex,
  setImageIndex,
  visible,
  referencePath,
  onRequestClose,
  ...props
}: ImageViewHeaderProps & {
  referencePath: string | undefined;
  visible: boolean;
}) {
  const { colors } = useTheme();
  const pager = React.useRef<PagerView>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [paginationEnabled, setPaginationEnabled] = React.useState(true);
  React.useEffect(() => {
    pager.current?.setPage(imageIndex);
  }, [imageIndex]);
  React.useEffect(
    () =>
      setImageIndex((index) =>
        index == null || !images.length
          ? index
          : index >= images.length
            ? images.length - 1
            : index,
      ),
    [images.length],
  );
  React.useEffect(() => setLoaded(false), [images, imageIndex, visible]);
  if (!visible) return null;
  if (!(images.length && imageIndex < images.length)) {
    onRequestClose();
    return null;
  }
  return (
    <Modal
      visible={visible}
      onRequestClose={onRequestClose}
      style={{
        width: "100%",
        height: "100%",
        marginTop: 20,
      }}
    >
      <SafeAreaView
        style={{
          height: "100%",
          backgroundColor: colors.background,
        }}
      >
        <ImageViewHeader
          images={images}
          imageIndex={imageIndex}
          setImageIndex={setImageIndex}
          onRequestClose={onRequestClose}
          loading={!loaded}
          {...props}
        />
        <PagerView
          ref={pager}
          initialPage={imageIndex}
          style={{ width: "100%", height: "100%" }}
          onPageSelected={(evt) => {
            setPaginationEnabled(true);
            setImageIndex(evt.nativeEvent.position);
          }}
          onDismiss={onRequestClose}
          scrollEnabled={paginationEnabled}
        >
          {images.map((image) => (
            <ZoomableImage
              key={image.id}
              doc={image}
              referencePath={referencePath}
              onLoad={() => setLoaded(true)}
            />
          ))}
        </PagerView>
      </SafeAreaView>
    </Modal>
  );
}
